import {
  Alert,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import * as React from "react";

import { RobotoTypography } from "@/shared/components";
import {
  ClockPreference,
  DatePreset,
  DateTimeFormatPreference,
  useDateTimeFormatPreference,
} from "@/shared/datetime";

import styles from "./DateTimeFormatPreferences.module.css";

function preferencesEqual(
  a: DateTimeFormatPreference,
  b: DateTimeFormatPreference,
): boolean {
  return a.datePreset === b.datePreset && a.clock === b.clock;
}

/**
 * Account → Preferences control for the user's global date/time display
 * format. Edits are held in a local draft and applied app-wide on Save (which
 * persists to user config; the {@link DateTimeFormatProvider} then publishes
 * the change to every timestamp on screen). A live preview reflects the draft.
 */
export function DateTimeFormatPreferences(): React.ReactElement {
  const {
    preference,
    isLoading,
    savePreference,
    isSaving,
    datePresetOptions,
    clockOptions,
    formatSample,
  } = useDateTimeFormatPreference();

  // `null` until the persisted value has hydrated; `preference` supplies the
  // default in the meantime so the controls are never uncontrolled.
  const [draft, setDraft] = React.useState<DateTimeFormatPreference | null>(
    null,
  );
  const [hydrated, setHydrated] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [savedToast, setSavedToast] = React.useState(false);

  React.useEffect(() => {
    if (!isLoading && !hydrated) {
      setDraft(preference);
      setHydrated(true);
    }
  }, [isLoading, hydrated, preference]);

  const effective = draft ?? preference;
  const isDirty = !preferencesEqual(effective, preference);

  const update = (patch: Partial<DateTimeFormatPreference>) => {
    setDraft({ ...effective, ...patch });
    setErrorMessage(null);
    setSavedToast(false);
  };

  const handleSave = async () => {
    setErrorMessage(null);
    try {
      await savePreference(effective);
      setSavedToast(true);
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to save your date & time format.",
      );
    }
  };

  const handleReset = () => {
    setDraft(preference);
    setErrorMessage(null);
    setSavedToast(false);
  };

  if (isLoading && !hydrated) {
    return (
      <div className={styles.section}>
        <CircularProgress size="1.5rem" />
      </div>
    );
  }

  const previewWithSeconds = formatSample(effective, { time: "seconds" });
  const previewDateOnly = formatSample(effective, { time: "none" });

  return (
    <div className={styles.section}>
      <div>
        <RobotoTypography variant="body1">
          Date &amp; time format
        </RobotoTypography>
        <RobotoTypography variant="body2" className={styles.hint}>
          Controls how dates and times are displayed throughout the app.
        </RobotoTypography>
      </div>

      {savedToast && (
        <Alert severity="success">Date &amp; time format saved.</Alert>
      )}
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <div className={styles.fieldGroup}>
        <RobotoTypography variant="subtitle2">Date format</RobotoTypography>
        <RadioGroup
          value={effective.datePreset}
          onChange={(event) =>
            update({ datePreset: event.target.value as DatePreset })
          }
        >
          {datePresetOptions.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio size="small" />}
              label={
                <span className={styles.presetLabel}>
                  <span>{option.label}</span>
                  <span className={styles.example}>
                    {formatSample(
                      { datePreset: option.value, clock: effective.clock },
                      { time: "none" },
                    )}
                  </span>
                </span>
              }
            />
          ))}
        </RadioGroup>
      </div>

      <div className={styles.fieldGroup}>
        <RobotoTypography variant="subtitle2">Clock</RobotoTypography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={effective.clock}
          onChange={(_event, value: ClockPreference | null) => {
            if (value !== null) {
              update({ clock: value });
            }
          }}
        >
          {clockOptions.map((option) => (
            <ToggleButton
              key={option.value}
              value={option.value}
              style={{ textTransform: "none" }}
            >
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>

      <div className={styles.fieldGroup}>
        <RobotoTypography variant="subtitle2">Preview</RobotoTypography>
        <div className={styles.preview}>
          <span className={styles.previewValue}>{previewWithSeconds}</span>
          <span className={`${styles.previewValue} ${styles.example}`}>
            {previewDateOnly}
          </span>
        </div>
      </div>

      <div className={styles.actions}>
        <Button
          variant="contained"
          disableElevation
          disabled={!isDirty || isSaving}
          onClick={() => {
            void handleSave();
          }}
          style={{ textTransform: "none" }}
        >
          {isSaving ? "Saving…" : "Save"}
        </Button>
        <Button
          variant="outlined"
          disabled={!isDirty || isSaving}
          onClick={handleReset}
          style={{ textTransform: "none" }}
        >
          Reset
        </Button>
      </div>
    </div>
  );
}
